// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/employee/actionCreator";
import {
  BlockUserAccount,
  UnblockUserAccount,
  ResetUserAccountPassword,
  GetUserRecentLogins,
  GetUserAccountChangelog,
  GetUserAccount,
  UpdateUserAccount,
  GetAccessLevelSchema,
  UpdateAccessLevel,
  GetUserAccountAssignments,
  UpdateUserAccountAssignments,
  ResetUserOtp,
  UnlockUserAccount,
} from "@modules/employee/constants/actions";
import type { HierarchyAssignment, HierarchyAssignmentSummary } from "@modules/service/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type { UserAccount } from "@modules/employee/reducer/user-account-details/types";

import { sortToString } from "@transformers/sort";
import { parseSchema } from "@utils/schema";

type UserAccountRequest = $Exact<{
  clientId?: string,
  userId: string,
}>;

export function* onBlockUserAccount({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.post, `/api/v1/user/${payload.userId}/login/disable`);

    yield all([
      put(actionCreators.BlockUserAccountSuccess(response.data)),
      put(
        actionCreators.GetUserAccount({
          clientId: payload.clientId,
          userId: payload.userId,
        }),
      ),
      put(
        actionCreators.GetUserRecentLogins({
          userId: payload.userId,
        }),
      ),
      put(sendSuccessMessage("USER_ACCOUNT_LOCKED")),
    ]);
  } catch (e) {
    yield put(actionCreators.BlockUserAccountError(e.response));
  }
}

export function* onUnblockUserAccount({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.post, `/api/v1/user/${payload.userId}/login/enable`);

    yield all([
      put(actionCreators.UnblockUserAccountSuccess(response.data)),
      put(
        actionCreators.GetUserAccount({
          clientId: payload.clientId,
          userId: payload.userId,
        }),
      ),
      put(
        actionCreators.GetUserRecentLogins({
          userId: payload.userId,
        }),
      ),
      put(sendSuccessMessage("USER_ACCOUNT_UNLOCKED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UnblockUserAccountError(e.response));
  }
}

export function* onResetUserAccountPassword({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.post, `/user/${payload.userId}/password-reset`);
    yield all([
      put(actionCreators.ResetUserAccountPasswordSuccess(response.data)),
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.userId,
          },
        }),
      ),
      put(sendSuccessMessage("EMAIL_PASSWORD_INSTRUCTIONS_SENT")),
    ]);
  } catch (e) {
    yield put(actionCreators.ResetUserAccountPasswordError(e.response));
  }
}

export function* onGetUserRecentLogins({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `/user/${payload.userId}/login/activity`, {
      params: {
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.GetUserRecentLoginsSuccess(response.data.content || []));
  } catch (e) {
    yield put(actionCreators.GetUserRecentLoginsError(e.response));
  }
}

export function* onGetUserAccountChangelog({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `/user/${payload.userId}/changelog`, {
      params: {
        limit: config.resultsPerPage,
      },
    });

    yield put(actionCreators.GetUserAccountChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetUserAccountChangelogError(e.response));
  }
}

export function* onGetUserAccount({ payload }: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/system-management/client/${payload.clientId}/user/${payload.userId}`,
    );

    yield put(actionCreators.GetUserAccountSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetUserAccountError(e.response));
  }
}

type UpdateUserAccountRequest = $Exact<{
  ...UserAccountRequest,
  data: UserAccount,
}>;

export function* onUpdateUserAccount({
  payload,
}: Action<UpdateUserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/system-management/client/${payload.clientId}/user/${payload.userId}`,
      payload.data,
    );

    yield all([
      put(actionCreators.UpdateUserAccountSuccess(response.data)),
      put(sendSuccessMessage("USER_ACCOUNT_UPDATED")),
      put(actionCreators.GetUserAccount(payload)),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateUserAccountError(e.response));
  }
}

type GetAccessLevelSchemaRequest = $Exact<{
  clientId: ?number,
  employeeId: ?number,
}>;

export function* onGetAccessLevelSchema({
  payload,
}: Action<GetAccessLevelSchemaRequest>): Generator<*, void, *> {
  try {
    const clientId = (payload || {}).clientId;
    const employeeId = (payload || {}).employeeId;
    if (!payload || !clientId || !employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("EmployeeAccessScopeDTO", { clientId: payload.clientId });
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/access-scope`,
    );

    yield all([
      put(actionCreators.GetAccessLevelSchemaSuccess(schema)),
      put(actionCreators.GetAccessLevelValueSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAccessLevelSchemaError(e.response));
  }
}

type UpdateAccessLevelRequest = $Exact<{
  clientId: ?number,
  employeeId: ?number,
  data: ?Object,
}>;

export function* onUpdateAccessLevel({
  payload,
}: Action<UpdateAccessLevelRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope`,
      payload.data,
    );

    yield all([
      put(actionCreators.UpdateAccessLevelSuccess(response.data)),
      put(
        actionCreators.GetUserAccount({ clientId: payload.clientId, userId: payload.employeeId }),
      ),
      put(sendSuccessMessage("USER_ACCESS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAccessLevelError(e.response));
  }
}

export function* onGetUserAccountAssignments({
  payload,
}: Action<UpdateAccessLevelRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope`,
    );
    yield put(actionCreators.GetUserAccountAssignmentsSuccess(response.data.hierarchyAssignments));
  } catch (e) {
    yield put(actionCreators.GetUserAccountAssignmentsError(e.response));
  }
}

type UpdateUserAccountAssignmentsRequest = $Exact<{
  clientId: number,
  employeeId: number,
  data: $Exact<{
    assignments: Array<HierarchyAssignment>,
    userType: string,
  }>,
}>;

export function* onUpdateUserAccountAssignments({
  payload,
}: Action<UpdateUserAccountAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope`,
    );

    if (
      prevAssignments &&
      prevAssignments.hierarchyAssignments &&
      prevAssignments.hierarchyAssignments.length
    ) {
      yield all([
        ...prevAssignments.hierarchyAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.data.assignments.length) {
      yield all([
        ...payload.data.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/access-scope/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(
        actionCreators.GetUserAccountAssignments({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(actionCreators.ToggleUserAccountAssignmentsModal(false)),
      put(actionCreators.UpdateUserAccountAssignmentsSuccess()),
      put(sendSuccessMessage("AREA_OF_ACCESS_SUCCESSFULLY_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateUserAccountAssignmentsError(e.response));
  }
}

export function* onResetUserOtp({
  payload,
}: Action<{ employeeId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.post, `/user/${payload.employeeId}/reset-otp`);

    yield all([
      put(actionCreators.ResetUserOtpSuccess(response.data)),
      put(sendSuccessMessage("RESET_OTP_SUCCESS")),
    ]);
  } catch (e) {
    yield put(actionCreators.ResetUserOtpError(e.response));
  }
}

export function* onUnlockUserAccount({
  payload,
}: Action<UserAccountRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.userId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/user/${payload.userId}/login/unlock-temporary-locked`,
    );

    yield all([
      put(actionCreators.UnlockUserAccountSuccess(response.data)),
      put(
        actionCreators.GetUserAccount({
          clientId: payload.clientId,
          userId: payload.userId,
        }),
      ),
      put(
        actionCreators.GetUserRecentLogins({
          userId: payload.userId,
        }),
      ),
      put(sendSuccessMessage("USER_ACCOUNT_UNLOCKED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UnlockUserAccountError(e.response));
  }
}

export function* UserAccountSagas(): Generator<*, void, *> {
  yield all([
    takeEvery(BlockUserAccount, onBlockUserAccount),
    takeEvery(UnblockUserAccount, onUnblockUserAccount),
    takeEvery(ResetUserAccountPassword, onResetUserAccountPassword),
    takeEvery(GetUserRecentLogins, onGetUserRecentLogins),
    takeEvery(GetUserAccountChangelog, onGetUserAccountChangelog),
    takeEvery(GetUserAccount, onGetUserAccount),
    takeEvery(UpdateUserAccount, onUpdateUserAccount),
    takeEvery(GetAccessLevelSchema, onGetAccessLevelSchema),
    takeEvery(UpdateAccessLevel, onUpdateAccessLevel),
    takeEvery(GetUserAccountAssignments, onGetUserAccountAssignments),
    takeEvery(UpdateUserAccountAssignments, onUpdateUserAccountAssignments),
    takeEvery(ResetUserOtp, onResetUserOtp),
    takeEvery(UnlockUserAccount, onUnlockUserAccount),
  ]);
}

export default UserAccountSagas;
